import {
  Box,
  useToast
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useState
} from "react";
import {
  addMinutes,
  isPast
} from "date-fns";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";

import { useConversationsApi } from "src/api/conversations/conversations.api";
import { Header } from "src/components/shared/Header";
import { Text } from "src/components/text";
import { pickerDateToUtcKey } from "src/utils/utcDate";

import { ConversationMode } from "../../../api";
import {
  CreateEditConversationForm,
  FormProps,
  SelectModeOption
} from "./components/CreateEditConversationForm";

export const CreateConversationPage: React.FC = () => {
  const toast = useToast();
  const history = useHistory();
  const [ selectedPartner, setSelectedParter ] = useState<string>();

  const [ mode, setMode ] = useState<SelectModeOption>({
    label: "Message",
    value: ConversationMode.MESSAGE
  });

  const [ createConversationRes, createConversationReq ] = useConversationsApi("CREATE_CONVERSATION");

  useEffect(() => {
    if (createConversationRes.data) {
      const defaultTopic = createConversationRes.data.topics?.[ 0 ];

      if (!createConversationRes.data._id || !defaultTopic) {
        toast({
          title: "Error creating conversation",
          description: "Conversation was created but couldn't get topic ID",
          status: "error"
        });
      } else {
        history.push(`/conversation/${createConversationRes.data._id}`);
      }
    }

    if (createConversationRes.error) {
      toast({
        title: "Error submitting form",
        description: createConversationRes.error.details,
        status: "error"
      });
    }
  }, [
    createConversationRes,
    toast,
    history
  ]);

  const {
    register, handleSubmit, watch, setValue
  } = useForm<FormProps>({
    defaultValues: {
      date: new Date(),
      startTime: new Date(addMinutes(new Date, 5)),
      endTime: new Date(addMinutes(new Date, 15)),
      latencySeconds: 10,
      latencyMinutes: 0
    }
  });

  const onSubmit = useCallback((data: FormProps) => {
    if (!data.date) {
      toast({
        title: "Invalid date",
        description: "Please select a date to continue",
        status: "error"
      });

      return;
    }

    if (selectedPartner) {
      const latencyDate = pickerDateToUtcKey(data.date);
      const startTime = data.date;

      startTime.setHours(data.startTime.getHours());
      startTime.setMinutes(data.startTime.getMinutes());
      startTime.setSeconds(0);

      const endTime = new Date(data.date);

      endTime.setHours(data.endTime.getHours());
      endTime.setMinutes(data.endTime.getMinutes());
      endTime.setSeconds(0);

      if (isPast(endTime) || isPast(startTime)) {
        toast({
          title: "Error submitting form",
          description: "Your dates / times have already past",
          status: "error"
        });
      } else {
        createConversationReq({
          date: new Date(data.date).toISOString(),
          latencyDate,
          latencySeconds: (data.latencyMinutes || 0) * 60 + (data.latencySeconds || 0),
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          participantIds: [ selectedPartner ],
          mode: mode.value
        });
      }
    } else {
      toast({
        title: "Invite somebody",
        description: "Please select a partner to continue",
        status: "error"
      });
    }
  }, [
    mode,
    createConversationReq,
    selectedPartner,
    toast
  ]);

  return (
    <Box p={10}>
      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          Create session
        </Text>
      </Box>

      <CreateEditConversationForm
        setMode={setMode}
        mode={mode}
        register={register}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        setSelectedParter={setSelectedParter}
        watch={watch}
        setValue={setValue}
        isNew
      />

    </Box>
  );
};